import { differenceInMinutes, differenceInSeconds } from 'date-fns'

import { formatDateWithoutWeek } from '@hapana/shared/utils/date.util'
import { EQUIPMENT_STATUSES } from '_constants/equipments'

const MINUTE_IN_MS = 60000

export const getClientName = (equipment) => {
  const client = equipment.assignments?.[0]?.client
  if (!client) {
    return '-'
  }
  return `${client.firstName || ''} ${client.lastName || ''}`.trim() || '-'
}

const getTimeAgoText = (date, now, t) => {
  const minutesSince = Math.max(0, differenceInMinutes(now, date))
  const secondsSince = Math.max(0, differenceInSeconds(now, date))
  return minutesSince === 0 && secondsSince > 0
    ? t('checkIn:equipments.manageEquipments.secAgo', { count: secondsSince })
    : t('checkIn:equipments.manageEquipments.minAgo', { count: minutesSince })
}

const parseTimestamp = (value) => {
  if (!value) {
    return null
  }
  const date = new Date(Number(value))
  return isNaN(date.getTime()) ? null : date
}

export const getTimeInfo = (equipment, now, t) => {
  const { status, assignments } = equipment
  const latest = assignments?.[0]

  if (status === EQUIPMENT_STATUSES.IN_USE || status === EQUIPMENT_STATUSES.DELAY) {
    const createdAt = parseTimestamp(latest?.createdAt)
    if (!createdAt) {
      return null
    }
    const endAt = new Date(
      createdAt.getTime() +
        (latest.timerMinutes || 0) * MINUTE_IN_MS +
        (latest.delayMinutes || 0) * MINUTE_IN_MS
    )
    const minLeft = Math.max(0, differenceInMinutes(endAt, now))
    const secLeft = Math.max(0, differenceInSeconds(endAt, now))

    const primary = getTimeAgoText(createdAt, now, t)
    const secondary =
      secLeft === 0
        ? t('checkIn:equipments.manageEquipments.timerFinished')
        : minLeft === 0
          ? t('checkIn:equipments.manageEquipments.timerTimeLeftSeconds', { seconds: secLeft })
          : t('checkIn:equipments.manageEquipments.timerTimeLeft', { time: minLeft })

    return { primary, secondary }
  }

  if (status === EQUIPMENT_STATUSES.DIRTY) {
    const doneAt = parseTimestamp(latest?.doneAt || latest?.cancelledAt || equipment.lastDirtyAt)
    if (!doneAt) {
      return null
    }
    return { primary: getTimeAgoText(doneAt, now, t) }
  }

  if (status === EQUIPMENT_STATUSES.AVAILABLE) {
    const lastUsedAt =
      parseTimestamp(equipment.lastInUseAt) ?? parseTimestamp(latest?.doneAt || latest?.cancelledAt)
    if (!lastUsedAt) {
      return null
    }
    const timeInfo = { primary: getTimeAgoText(lastUsedAt, now, t) }
    if (latest?.timerMinutes) {
      timeInfo.secondary = t('checkIn:equipments.manageEquipments.tanTime', {
        minutes: latest.timerMinutes,
      })
    }
    return timeInfo
  }

  if (status === EQUIPMENT_STATUSES.DISABLED || status === EQUIPMENT_STATUSES.ERROR) {
    if (!latest?.createdAt) {
      return null
    }
    return {
      primary: t('checkIn:equipments.manageEquipments.lastUsedOn', {
        date: formatDateWithoutWeek(Number(latest.createdAt)),
      }),
    }
  }

  return null
}
